import React from 'react';
import { ActionIcon, Checkbox, Indicator, Select, Tooltip } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen as faPenDef,
  faTags as faTagsDef,
  faBan as faBanDef,
  faCircleCheck as faCircleCheckDef,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { CategoryReportGroup, CategoryView } from '../../../../../../services/categories/categories.service';
import shared from '../../shared/settings-shared.module.scss';
import productClasses from '../../shared/product-settings.module.scss';

const faPen = faPenDef as IconProp;
const faTags = faTagsDef as IconProp;
const faBan = faBanDef as IconProp;
const faCircleCheck = faCircleCheckDef as IconProp;

export type CategoryManagerRowProps = {
  category: CategoryView;
  depthPrefix: string;
  parentOptions: { value: string; label: string }[];
  productCount: number;
  isWriteInFlight: boolean;
  isToggling: boolean;
  reorderMode: boolean;
  rowRef?: (element: HTMLElement | null) => void;
  rowStyle?: React.CSSProperties;
  dragHandle?: React.ReactNode;
  onStartEdit: (category: CategoryView) => void;
  onSetParent: (category: CategoryView, parentCategoryPk: number | null) => void;
  onSetReportGroup: (category: CategoryView, reportGroup: CategoryReportGroup) => void;
  onOpenMembership: (categoryId: number) => void;
  onToggleActive: (category: CategoryView) => void;
};

// Architectural summary: presentational category row; no dnd hooks (those live in the sortable wrapper).
const CategoryManagerRowComponent: React.FC<CategoryManagerRowProps> = ({
  category,
  depthPrefix,
  parentOptions,
  productCount,
  isWriteInFlight,
  isToggling,
  reorderMode,
  rowRef,
  rowStyle,
  dragHandle,
  onStartEdit,
  onSetParent,
  onSetReportGroup,
  onOpenMembership,
  onToggleActive,
}) => (
  <tr
    ref={rowRef}
    style={rowStyle}
    data-testid={`manage-category-${category.id}`}
    className={reorderMode ? productClasses.reorderRow : ''}
  >
    <td className={productClasses.dragCell} data-label="">{reorderMode ? dragHandle : null}</td>
    <td data-label="שם" data-testid={`manage-category-name-${category.id}`}>
      {depthPrefix}
      {category.name}
    </td>
    <td className={shared.parentCell} data-label="קטגוריית אם">
      {reorderMode ? null : (
        <Select
          data-testid={`category-manager-parent-${category.id}`}
          aria-label={`קטגוריית אם עבור ${category.name}`}
          placeholder="ללא (קטגוריה ראשית)"
          clearable
          radius="md"
          size="sm"
          data={parentOptions}
          value={category.parentCategoryPk === null ? null : String(category.parentCategoryPk)}
          onChange={(value) => onSetParent(category, value === null ? null : Number(value))}
          disabled={isWriteInFlight}
        />
      )}
    </td>
    <td className={shared.checkboxCell} data-label="אוכל">
      {reorderMode ? null : (
        <Checkbox
          data-testid={`category-manager-food-${category.id}`}
          aria-label={`סמן אוכל עבור ${category.name}`}
          checked={category.reportGroup === 'food'}
          disabled={isWriteInFlight}
          onChange={() => onSetReportGroup(category, 'food')}
          size="md"
        />
      )}
    </td>
    <td className={shared.checkboxCell} data-label="משקאות">
      {reorderMode ? null : (
        <Checkbox
          data-testid={`category-manager-beverages-${category.id}`}
          aria-label={`סמן משקאות עבור ${category.name}`}
          checked={category.reportGroup === 'beverages'}
          disabled={isWriteInFlight}
          onChange={() => onSetReportGroup(category, 'beverages')}
          size="md"
        />
      )}
    </td>
    <td data-label="סטטוס">
      <span className={category.isActive ? shared.statusActive : shared.statusInactive}>
        {category.isActive ? 'פעיל' : 'לא פעיל'}
      </span>
    </td>
    <td data-label="פעולות" className={productClasses.categoryActionsCell}>
      {reorderMode ? null : (
        <div className={shared.actionsCell}>
          <Tooltip label="שנה שם" withArrow>
            <ActionIcon
              aria-label="שנה שם"
              data-testid={`category-manager-edit-${category.id}`}
              variant="subtle"
              color="gray"
              radius="md"
              size="lg"
              disabled={isWriteInFlight}
              onClick={() => onStartEdit(category)}
            >
              <FontAwesomeIcon icon={faPen} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="שייך מוצרים" withArrow>
            <Indicator label={productCount} disabled={!productCount} color="blue" size={18} offset={4}>
              <ActionIcon
                aria-label="שייך מוצרים"
                data-testid={`category-manager-membership-${category.id}`}
                variant="subtle"
                color="blue"
                radius="md"
                size="lg"
                disabled={isWriteInFlight}
                onClick={() => onOpenMembership(category.id)}
              >
                <FontAwesomeIcon icon={faTags} />
              </ActionIcon>
            </Indicator>
          </Tooltip>

          {category.isActive ? (
            <Tooltip label="השבת" withArrow>
              <ActionIcon
                aria-label="השבת"
                data-testid={`category-manager-toggle-${category.id}`}
                variant="subtle"
                color="red"
                radius="md"
                size="lg"
                className={productClasses.statusActionIcon}
                loading={isToggling}
                disabled={isWriteInFlight}
                onClick={() => onToggleActive(category)}
              >
                <FontAwesomeIcon icon={faBan} />
              </ActionIcon>
            </Tooltip>
          ) : (
            <Tooltip label="הפעל" withArrow>
              <ActionIcon
                aria-label="הפעל"
                data-testid={`category-manager-toggle-${category.id}`}
                variant="subtle"
                color="green"
                radius="md"
                size="lg"
                className={productClasses.statusActionIcon}
                loading={isToggling}
                disabled={isWriteInFlight}
                onClick={() => onToggleActive(category)}
              >
                <FontAwesomeIcon icon={faCircleCheck} />
              </ActionIcon>
            </Tooltip>
          )}
        </div>
      )}
    </td>
  </tr>
);

export const CategoryManagerRow = React.memo(CategoryManagerRowComponent);
